import React from 'react';
import PropTypes from 'prop-types';
import { api } from '../../api';
import Swal from 'sweetalert2';
import useFormDraft from '../../hooks/useFormDraft';
import LoadDraftButton from '../../components/LoadDraftButton';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import MDBox from 'components/MDBox';
import RichTextEditor from 'components/RichTextEditor';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';

// Make sure these match exactly the values coming from the API
const INFERTILITY_TYPE_OPTIONS = [
  { value: 'primary', label: 'Primary Infertility' },
  { value: 'secondary', label: 'Secondary Infertility' },
  { value: 'boh', label: 'BOH' },
  { value: 'gynecological', label: 'Gynecological Problems' },
];

const selectBoxHeight = 40;

// Plain list of selectable years for Miscarriage Year — no date/calendar involved,
// year_of_miscarriage is stored as a bare integer.
const MISCARRIAGE_YEAR_SPAN = 60;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: MISCARRIAGE_YEAR_SPAN }, (_, i) => CURRENT_YEAR - i);

function ucwords(str) {
  return str.replace(/\b\w/g, (l) => l.toUpperCase());
}

// Helper function to check if D&E should be disabled based on miscarriage type
const isDEDisabledForType = (miscarriageType) => {
  const disabledTypes = [
    'LUCS',
    'PTL',
    'RL Tubal Ectopic',
    'LT Tubal Ectopic',
    'Ectopic-B/L',
    'Normal Delivery'
  ];
  return disabledTypes.some(type => 
    miscarriageType?.toLowerCase().includes(type.toLowerCase())
  );
};

function ObstetricHistoryForm({ 
  loading, 
  onSubmit, 
  onDeleteInfertility,
  onDeleteMiscarriage,
  onEdit,
  patientId,
  obstetricHistory,
  typeOfPregnancy = [],
  typeOfMiscarriage = [],
  miscarriageHistories = []
}) {
  console.log('ObstetricHistoryForm props:', { obstetricHistory });
  console.log('Type of Pregnancy:', typeOfPregnancy);
  console.log('Type of Miscarriage:', typeOfMiscarriage);
  console.log('Miscarriage Histories:', miscarriageHistories);

  // Draft management for infertility form
  const infertilityDraft = useFormDraft(`patient_history_infertility_${patientId}`, 1000);
  
  // Draft management for miscarriage form
  const miscarriageDraft = useFormDraft(`patient_history_miscarriage_${patientId}`, 1000);

  // Infertility form state - using correct API parameter name
  const [infertilityType, setInfertilityType] = React.useState(obstetricHistory?.infetrtility_type || '');
  const [paraP1, setParaP1] = React.useState(obstetricHistory?.para_p1 || '');
  const [paraP2, setParaP2] = React.useState(obstetricHistory?.para_p2 || '');
  const [noOfAbortion, setNoOfAbortion] = React.useState(obstetricHistory?.no_of_miscarriages?.toString() || '');
  const [infertilityRemarks, setInfertilityRemarks] = React.useState(obstetricHistory?.remarks || '');
  const [liveIssues, setLiveIssues] = React.useState(obstetricHistory?.live_issues || '');

  // Handler to load infertility draft on button click
  const handleLoadInfertilityDraft = async () => {
    const draftData = await infertilityDraft.loadDraftData();
    if (draftData) {
      setInfertilityType(draftData.infertilityType || '');
      setParaP1(draftData.paraP1 || '');
      setParaP2(draftData.paraP2 || '');
      setNoOfAbortion(draftData.noOfAbortion || '');
      setInfertilityRemarks(draftData.infertilityRemarks || '');
      setLiveIssues(draftData.liveIssues || '');
    }
  };

  // Auto-save infertility form data
  React.useEffect(() => {
    const formData = {
      infertilityType,
      paraP1,
      paraP2,
      noOfAbortion,
      infertilityRemarks,
      liveIssues,
    };
    
    // Only auto-save if form has some data
    if (infertilityType || paraP1 || paraP2 || noOfAbortion || infertilityRemarks || liveIssues) {
      infertilityDraft.autoSave(formData);
    }
  }, [infertilityType, paraP1, paraP2, noOfAbortion, infertilityRemarks, liveIssues]);

  // Update infertility form fields when obstetricHistory changes
  React.useEffect(() => {
    if (obstetricHistory) {
      console.log('Setting infertility type from:', obstetricHistory.infetrtility_type); 
      console.log('Available infertility options:', INFERTILITY_TYPE_OPTIONS);
      setInfertilityType(obstetricHistory.infetrtility_type || '');
      setParaP1(obstetricHistory.para_p1 || '');
      setParaP2(obstetricHistory.para_p2 || '');
      setNoOfAbortion(obstetricHistory.no_of_miscarriages?.toString() || '');
      setInfertilityRemarks(obstetricHistory.remarks || '');
      setLiveIssues(obstetricHistory.live_issues || '');
    }
  }, [obstetricHistory]);

  // Determine if form should be locked (when infertility type is primary)
  const isPrimaryInfertility = infertilityType && infertilityType.toLowerCase() === 'primary';
  const formLocked = isPrimaryInfertility;

  // Effect to handle Primary infertility logic
  React.useEffect(() => {
    if (isPrimaryInfertility) {
      // Set default values for Primary infertility
      setParaP1('0');
      setParaP2('0');
      setNoOfAbortion('0');
    }
  }, [isPrimaryInfertility]);

  // Calculate the next para number based on existing miscarriage histories
  const getNextParaNumber = () => {
    if (!miscarriageHistories || miscarriageHistories.length === 0) {
      return 'P1';
    }
    
    // Find the maximum para number from existing records
    const maxParaNum = miscarriageHistories.reduce((max, history) => {
      const paraNum = history.para_number || '';
      const num = parseInt(paraNum.replace(/\D/g, '')) || 0;
      return Math.max(max, num);
    }, 0);
    
    return `P${maxParaNum + 1}`;
  };

  // Miscarriage form state - Start with next available para number
  const [rows, setRows] = React.useState([{
    paraNumber: getNextParaNumber(),
    yearOfMiscarriage: '',
    typeOfMiscarriage: '',
    wksOfMiscarriage: '',
    deDone: false,
    pregType: '',
    remarks: '',
  }]);

  // Handler to load miscarriage draft on button click
  const handleLoadMiscarriageDraft = async () => {
    const draftData = await miscarriageDraft.loadDraftData();
    if (draftData && draftData.rows) {
      setRows(draftData.rows);
    }
  };

  // Auto-save miscarriage form data
  React.useEffect(() => {
    const formData = { rows };
    
    // Only auto-save if there's data in rows
    const hasData = rows.some(row => 
      row.yearOfMiscarriage || row.typeOfMiscarriage || row.wksOfMiscarriage || row.remarks
    );
    
    if (hasData) {
      miscarriageDraft.autoSave(formData);
    }
  }, [rows]);

  // Update initial para number when miscarriageHistories changes
  React.useEffect(() => {
    const nextParaNumber = getNextParaNumber();
    setRows([{
      paraNumber: nextParaNumber,
      yearOfMiscarriage: '',
      typeOfMiscarriage: '',
      wksOfMiscarriage: '',
      deDone: false,
      pregType: '',
      remarks: '',
    }]);
  }, [miscarriageHistories.length]);

  // Edit state for inline editing
  const [editingRecord, setEditingRecord] = React.useState(null);

  // Remove the useEffect that was populating rows from miscarriageHistories
  // This ensures the form always starts blank
  /*
  React.useEffect(() => {
    if (miscarriageHistories.length > 0) {
      setRows(miscarriageHistories.map(history => {
        console.log('Mapping miscarriage history:', history);
        return {
          id: history.id,
          yearOfMiscarriage: history.year_of_miscarriage || '',
          typeOfMiscarriage: history.param_group_member_option.value || '',
          wksOfMiscarriage: history.weeks_of_miscarriage || '',
          deDone: history.de_done || false,
          pregType: history.pregnancy_type || '',
        };
      }));
    }
  }, [miscarriageHistories]);
  */

  // Handler for infertility form submission
  const handleInfertilitySubmit = async (e) => {
    e.preventDefault();
    
    // Debug logging
    console.log('Form values:', { infertilityType, paraP1, paraP2, noOfAbortion });
    console.log('Validation checks:', {
      infertilityType: !infertilityType,
      paraP1: paraP1 === '',
      paraP2: paraP2 === '',
      noOfAbortion: noOfAbortion === ''
    });
    
    // Validate required fields - allow 0 as valid value
    if (!infertilityType || paraP1 === '' || paraP2 === '') {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill in all required fields for infertility data.',
      });
      return;
    }

    // Prepare data for API call
    const infertilityData = {
      contact_id: patientId,
      infertility_type: infertilityType,
      para_p1: paraP1,
      para_p2: paraP2,
      no_of_miscarriages: noOfAbortion ? parseInt(noOfAbortion, 10): 0,
      remarks: infertilityRemarks,
      live_issues: liveIssues,
    };

    try {
      console.log('Sending infertility data to API:', infertilityData);
      
      // Call API to save infertility record using draft handler
      const result = await infertilityDraft.handleSubmitWithDraft(
        async (data) => {
          const response = await api.opd.createObstetricRecord(data);
          return response;
        },
        infertilityData
      );
      
      if (result?.data) {
        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Infertility data saved successfully.',
          timer: 3000,
          showConfirmButton: false
        });

        // Call parent onSubmit callback with type identifier
        onSubmit && onSubmit(result.data, 'infertility'); // Pass type to identify which data was saved
      }
    } catch (error) {
      console.error('Error saving infertility data:', error);
      
      // Show error message
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to save infertility data. Please try again.',
      });
    }
  };

  // Handler for miscarriage form submission
  const handleMiscarriageSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Filter out empty rows (rows without required data)
      // Note: pregType removed from required validation
      // weeks can be 0, so we check if it's not empty string and not undefined
      const validRows = rows.filter(row => 
        row.yearOfMiscarriage && 
        row.typeOfMiscarriage && 
        (row.wksOfMiscarriage === 0 || row.wksOfMiscarriage)
      );

      // Check if there are valid rows to save
      if (validRows.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'No Data',
          text: 'Please fill in Year, Type, and Weeks for at least one miscarriage record.',
        });
        return;
      }

      // Transform the form data to match the API format
      const miscarriageData = validRows.map((row, index) => {
        console.log('Processing row for submission:', row);
        
        // Use param IDs from row data (populated when user selects miscarriage type)
        // For new rows, these IDs come from the typeOfMiscarriage options
        const paramGroupId = row.param_group_id || 7; // Fallback to default group ID
        const paramGroupMemberId = row.param_group_member_id || 19; // Fallback to default member ID
        
        // For new rows without existing option ID, find it from typeOfMiscarriage array
        let paramGroupMemberOptionId = row.param_group_member_option_id;
        if (!paramGroupMemberOptionId && row.typeOfMiscarriage) {
          const selectedOption = typeOfMiscarriage.find(option => option.value === row.typeOfMiscarriage);
          paramGroupMemberOptionId = selectedOption?.param_group_member_option_id;
        }
        
        return {
          contact_id: patientId, // Patient ID passed as prop
          year_of_miscarriage: parseInt(row.yearOfMiscarriage, 10), // Convert year to integer
          para_number: row.paraNumber || `P${index + 1}`, // Para number from form
          param_group_id: parseInt(paramGroupId, 10), // Use param_group_id from row or fallback
          param_group_member_id: parseInt(paramGroupMemberId, 10), // Use param_group_member_id from row or fallback
          param_group_member_option_id: parseInt(paramGroupMemberOptionId, 10), // Use param_group_member_option_id from selection
          value: `Miscarriage ${index + 1}`, // Generate a meaningful value description
          weeks_of_miscarriage: parseInt(row.wksOfMiscarriage, 10), // Convert weeks to integer
          pregnancy_type: row.pregType, // Pregnancy type from form
          de_done: Boolean(row.deDone), // Always send boolean - true or false
          remarks: row.remarks && row.remarks.trim() ? row.remarks : null, // Send null if remarks is blank
        };
      });

      console.log('Sending miscarriage data to API:', miscarriageData);

      // Call the API to save miscarriage records using draft handler
      const result = await miscarriageDraft.handleSubmitWithDraft(
        async (data) => {
          const response = await api.opd.createMiscarriageRecord(data);
          return response;
        },
        miscarriageData
      );
      
      if (result?.data) {
        // Show success message using SweetAlert
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `${validRows.length} miscarriage record(s) saved successfully.`,
          timer: 3000,
          showConfirmButton: false
        });

        // Reset the miscarriage form to single blank row after successful save
        setRows([{
          paraNumber: 'P1',
          yearOfMiscarriage: '',
          typeOfMiscarriage: '',
          wksOfMiscarriage: '',
          deDone: false,
          pregType: '',
          remarks: '',
        }]);

        // Call the parent onSubmit callback with the response data
        // This should trigger a reload of the obstetric history data in the parent component
        onSubmit && onSubmit(result.data, 'miscarriage'); // Pass type to identify which data was saved
      }
    } catch (error) {
      console.error('Error saving miscarriage records:', error);
      
      // Show error message using SweetAlert
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to save miscarriage records. Please try again.',
      });
    }
  };

  // Update a specific miscarriage row field
  const handleRowChange = (idx, field, value) => {
    setRows((prev) => prev.map((row, i) => {
      if (i === idx) {
        const updatedRow = { ...row, [field]: value };
        
        // When user selects a miscarriage type, also store the related param IDs
        if (field === 'typeOfMiscarriage' && value) {
          const selectedOption = typeOfMiscarriage.find(option => option.value === value);
          if (selectedOption) {
            console.log('Storing param IDs for selected option:', selectedOption);
            // Store the param IDs from the selected miscarriage type option
            updatedRow.param_group_id = selectedOption.param_group_id;
            updatedRow.param_group_member_id = selectedOption.param_group_member_id;
            updatedRow.param_group_member_option_id = selectedOption.param_group_member_option_id;
            
            // If D&E should be disabled for this type, set it to false
            if (isDEDisabledForType(selectedOption.label)) {
              updatedRow.deDone = false;
            }
          }
        }
        
        return updatedRow;
      }
      return row;
    }));
  };

  // Add new blank row when plus icon is clicked
  const handleAddRow = () => {
    // Calculate max allowed miscarriages = (P1 + P2)
    const maxAllowedMiscarriages = (parseInt(paraP1) || 0) + (parseInt(paraP2) || 0);
    // Count existing miscarriage records (saved ones)
    const existingMiscarriageCount = miscarriageHistories.length;
    // Count current form rows (unsaved ones)
    const currentFormRowsCount = rows.length;
    
    // Total miscarriages would be existing + current rows
    const totalMiscarriages = existingMiscarriageCount + currentFormRowsCount;
    
    // Check if we've reached the limit
    if (totalMiscarriages >= maxAllowedMiscarriages) {
      alert(`Maximum ${maxAllowedMiscarriages} miscarriage records allowed based on Para values (${paraP1} + ${paraP2} = ${maxAllowedMiscarriages}). You already have ${existingMiscarriageCount} saved records and ${currentFormRowsCount} form entries.`);
      return;
    }
    
    // Auto-generate para number for new row
    const nextParaNumber = `P${totalMiscarriages + 1}`;
    
    setRows((prev) => [
      ...prev,
      {
        paraNumber: nextParaNumber,
        yearOfMiscarriage: '',
        typeOfMiscarriage: '',
        wksOfMiscarriage: '',
        deDone: false,
        pregType: '',
        remarks: '',
      },
    ]);
  };

  // Remove a miscarriage row
  const handleRemoveRow = (idx) => {
    // Ensure at least one row remains
    if (rows.length > 1) {
      setRows((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  // Render infertility information as a separate table
  const renderInfertilityTable = (obstetricHistory, onDelete) => {
    if (!obstetricHistory) return null;
    return (
      <MDBox mb={4}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00798E', fontWeight: 'bold' }}>
          Infertility Information
        </Typography>
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Infertility Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Para</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Para</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E', display: 'none' }}>No Of Abortion</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E', display: 'none' }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E', display: 'none' }}>Live Issues</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Actions</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Typography variant="body2">{ucwords(obstetricHistory.infetrtility_type || '-')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{obstetricHistory.para_p1 || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{obstetricHistory.para_p2 || '-'}</Typography>
                </TableCell>
                <TableCell sx={{ display: 'none' }}>
                  <Typography variant="body2">{obstetricHistory.no_of_miscarriages || '-'}</Typography>
                </TableCell>
                <TableCell sx={{ display: 'none' }}>
                  <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }} dangerouslySetInnerHTML={{ __html: obstetricHistory.remarks || '-' }} />
                </TableCell>
                <TableCell sx={{ display: 'none' }}>
                  <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }} dangerouslySetInnerHTML={{ __html: obstetricHistory.live_issues || '-' }} />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label="delete"
                    color="error"
                    size="small"
                    onClick={() => onDelete && onDelete(obstetricHistory)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  // Render previous miscarriage history records in a separate table
  const renderPreviousTable = (miscarriageData, onDelete, onEdit) => {
    if (!miscarriageData || miscarriageData.length === 0) return null;

    // Sort miscarriage data by para_number (P1, P2, P3, etc.)
    const sortedMiscarriageData = [...miscarriageData].sort((a, b) => {
      const paraA = a.para_number || '';
      const paraB = b.para_number || '';
      
      // Extract numeric part from para_number (e.g., "P1" -> 1, "P2" -> 2)
      const numA = parseInt(paraA.replace(/\D/g, '')) || 0;
      const numB = parseInt(paraB.replace(/\D/g, '')) || 0;
      
      return numA - numB;
    });

    const data = sortedMiscarriageData.map((miscarriage) => ({
      id: miscarriage.id,
      para_number: miscarriage.para_number || '',
      year_of_miscarriage: miscarriage.year_of_miscarriage || '',
      type_of_miscarriage: miscarriage.param_group_member_option?.value || miscarriage.type_of_miscarriage || '',
      weeks_of_miscarriage: miscarriage.weeks_of_miscarriage || '',
      de_done: miscarriage.de_done || false,
      pregnancy_type: miscarriage.pregnancy_type || '',
      remarks: miscarriage.remarks || '',
      value: miscarriage.value || miscarriage.param_group_member_option?.value || '',
      irm_contacts_id: miscarriage.irm_contacts_id || '',
      param_group_id: miscarriage.irm_param_groups_id || '',
      param_group_member_id: miscarriage.irm_param_group_members_id || '',
      param_group_member_option_id: miscarriage.irm_param_group_member_options_id || null,
    }));

    return (
      <MDBox mb={4}>
        <Typography variant="h6" gutterBottom sx={{ color: '#00798E', fontWeight: 'bold' }}>
          Miscarriage History Records
        </Typography>
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Para</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Year</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Weeks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>D+E Done</TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Preg Type</TableCell> */}
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Remarks</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#00798E' }}>Actions</TableCell>
              </TableRow>
              {data.map((row, idx) => {
                const isEditing = editingRecord?.id === row.id;
                return (
                  <TableRow
                    key={row.id || idx}
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: isEditing ? '#fff3e0' : '#fafafa' },
                      '&:hover': { backgroundColor: isEditing ? '#ffe0b2' : '#e3f2fd' },
                      backgroundColor: isEditing ? '#fff8e1' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#00798E' }}>
                        {row.para_number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          select
                          size="small"
                          value={editingRecord.year_of_miscarriage || ''}
                          onChange={(e) => setEditingRecord({ ...editingRecord, year_of_miscarriage: e.target.value ? parseInt(e.target.value, 10) : '' })}
                          sx={{ width: 110 }}
                        >
                          <MenuItem value="" disabled><em>Year</em></MenuItem>
                          {YEAR_OPTIONS.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <Typography variant="body2">{row.year_of_miscarriage || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editingRecord.param_group_member_option_id || ''}
                            onChange={(e) => {
                              const selected = typeOfMiscarriage.find((opt) => opt.param_group_member_option_id === e.target.value);
                              const updatedRecord = {
                                ...editingRecord,
                                param_group_member_option_id: e.target.value,
                                param_group_member_id: selected?.param_group_member_id || editingRecord.param_group_member_id,
                                type_of_miscarriage: selected?.label || '',
                                value: selected?.value || '',
                              };
                              // If D&E should be disabled for this type, set it to false
                              if (isDEDisabledForType(selected?.label)) {
                                updatedRecord.de_done = false;
                              }
                              setEditingRecord(updatedRecord);
                            }}
                            sx={{ height: 40 }}
                          >
                            {typeOfMiscarriage?.map((option) => (
                              <MenuItem key={option.param_group_member_option_id} value={option.param_group_member_option_id}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2">{ucwords(row.type_of_miscarriage || '-')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={editingRecord.weeks_of_miscarriage || ''}
                            onChange={(e) => setEditingRecord({ ...editingRecord, weeks_of_miscarriage: e.target.value })}
                            sx={{ height: 40, width: 80 }}
                          >
                            {Array.from({ length: 41 }, (_, i) => i).map((week) => (
                              <MenuItem key={week} value={week}>
                                {week}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2">{row.weeks_of_miscarriage || '-'}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Switch
                          checked={editingRecord.de_done || false}
                          onChange={(e) => setEditingRecord({ ...editingRecord, de_done: e.target.checked })}
                          size="small"
                          disabled={isDEDisabledForType(editingRecord.type_of_miscarriage)}
                        />
                      ) : (
                        <Typography variant="body2">
                          {isDEDisabledForType(row.type_of_miscarriage) ? '-' : 
                           (row.de_done === true || row.de_done === 'true' ? 'Yes' : 'No')}
                        </Typography>
                      )}
                    </TableCell>
                    {/* Preg Type Column - Commented Out */}
                    {/* <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={editingRecord.pregnancy_type || ''}
                          onChange={(e) => setEditingRecord({ ...editingRecord, pregnancy_type: e.target.value })}
                          sx={{ width: 120 }}
                        />
                      ) : (
                        <Typography variant="body2">{row.pregnancy_type || '-'}</Typography>
                      )}
                    </TableCell> */}
                    <TableCell>
                      {isEditing ? (
                        <RichTextEditor value={editingRecord.remarks || ''} onChange={(value) => setEditingRecord({ ...editingRecord, remarks: value })} height="80px" />
                      ) : (
                        <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} dangerouslySetInnerHTML={{ __html: row.remarks || '-' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton aria-label="save" color="success" size="small" onClick={async () => {
                            const result = await Swal.fire({ title: 'Save Changes?', text: 'Do you want to update this record?', icon: 'question', showCancelButton: true, confirmButtonColor: '#00798E', cancelButtonColor: '#999', confirmButtonText: 'Yes, update it!' });
                            if (result.isConfirmed) {
                              onEdit?.(editingRecord);
                              setEditingRecord(null);
                            }
                          }} sx={{ '&:hover': { backgroundColor: '#e8f5e9', transform: 'scale(1.1)', transition: 'all 0.2s ease' } }}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton aria-label="cancel" color="default" size="small" onClick={() => setEditingRecord(null)} sx={{ '&:hover': { backgroundColor: '#f5f5f5', transform: 'scale(1.1)', transition: 'all 0.2s ease' } }}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton aria-label="edit" color="warning" size="small" onClick={() => setEditingRecord({ ...row })} sx={{ '&:hover': { backgroundColor: '#fff3e0', transform: 'scale(1.1)', transition: 'all 0.2s ease' } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton aria-label="delete" color="error" size="small" onClick={() => onDelete && onDelete(row)} sx={{ '&:hover': { backgroundColor: '#ffebee', transform: 'scale(1.1)', transition: 'all 0.2s ease' } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  return (
    <MDBox>
      {/* Existing Infertility & Previous Records Tables */}
      {renderInfertilityTable(obstetricHistory, onDeleteInfertility)}

      {/* Infertility Form Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#00798E', fontWeight: 'bold' }}>
            Infertility Information
          </Typography>
          <LoadDraftButton
            hasDraftData={infertilityDraft.hasDraftData}
            onLoadDraft={handleLoadInfertilityDraft}
            onClearDraft={async () => await infertilityDraft.clearDraft()}
            timestamp={infertilityDraft.getFormattedTimestamp()}
          />
        </Box>
        {isPrimaryInfertility && (
          <Typography variant="body2" sx={{ mb: 2, color: '#666', fontStyle: 'italic' }}>
            Note: Para fields are automatically set to 0 for Primary infertility and cannot be modified.
          </Typography>
        )}
        <form onSubmit={handleInfertilitySubmit}>
          <Grid container spacing={2} alignItems="center">
            {/* First Row: All fields in single row */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Infertility Type</InputLabel>
                <Select
                  value={infertilityType || ''}
                  label="Infertility Type"
                  onChange={(e) => setInfertilityType(e.target.value)}
                  required
                  disabled={loading}
                  sx={{ height: selectBoxHeight }}
                >
                  <MenuItem value="">
                    <em>Select Infertility Type</em>
                  </MenuItem>
                  {INFERTILITY_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField
                value={paraP1}
                onChange={(e) => setParaP1(e.target.value)}
                label="Para (No)"
                type="number"
                fullWidth
                size="small"
                disabled={loading || formLocked}
                required
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <TextField
                value={paraP2}
                onChange={(e) => setParaP2(e.target.value)}
                label="Para (No)"
                type="number"
                fullWidth
                size="small"
                disabled={loading || formLocked}
                required
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <RichTextEditor
                value={infertilityRemarks}
                onChange={(value) => setInfertilityRemarks(value)}
                label="Remarks"
                height="44px"
                placeholder="Brief remarks about infertility..."
              />
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="submit"
                disabled={loading || !infertilityType || !paraP1 || !paraP2}
                style={{
                  padding: '10px 20px',
                  background: '#00798E',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: (loading || !infertilityType || !paraP1 || !paraP2) ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  opacity: (loading || !infertilityType || !paraP1 || !paraP2) ? 0.6 : 1,
                  whiteSpace: 'nowrap',
                  height: '40px',
                  marginTop: '4px',
                }}
              >
                Save
              </button>
            </Grid>
          </Grid>
        </form>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Miscarriage Form Section - Only show if Para P1 or P2 > 0 and not Primary infertility */}
      {((parseInt(paraP1) > 0 || parseInt(paraP2) > 0) && !isPrimaryInfertility) && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#00798E', fontWeight: 'bold' }}>
              Miscarriage History
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                Maximum {(parseInt(paraP1) || 0) + (parseInt(paraP2) || 0)} records allowed (Based on Para: {paraP1 || 0} + {paraP2 || 0})
              </Typography>
              <LoadDraftButton
                hasDraftData={miscarriageDraft.hasDraftData}
                onLoadDraft={handleLoadMiscarriageDraft}
                onClearDraft={async () => await miscarriageDraft.clearDraft()}
                timestamp={miscarriageDraft.getFormattedTimestamp()}
              />
            </Box>
          </Box>

          {/* Show existing miscarriage records in table format */}
          {renderPreviousTable(miscarriageHistories, onDeleteMiscarriage, onEdit)}

          {/* Calculate if we can show the form for new entries */}
          {(() => {
            const maxAllowedMiscarriages = (parseInt(paraP1) || 0) + (parseInt(paraP2) || 0);
            const existingMiscarriageCount = miscarriageHistories.length;
            const currentFormRowsCount = rows.length;
            const totalMiscarriages = existingMiscarriageCount + currentFormRowsCount;
            
            // Only show form if there's still capacity for more miscarriages
            if (existingMiscarriageCount >= maxAllowedMiscarriages) {
              return (
                <Typography variant="body2" sx={{ mt: 2, color: '#f44336', fontStyle: 'italic', textAlign: 'center' }}>
                  All {maxAllowedMiscarriages} miscarriage records have been added. Cannot add more.
                </Typography>
              );
            }

            return (
        <form onSubmit={handleMiscarriageSubmit}>
          {rows.map((row, idx) => (
            <Box 
              key={idx} 
              sx={{ 
                mb: 3, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 2,
                backgroundColor: '#fafafa'
              }}
            >
              <Grid container spacing={2} alignItems="center">
                {/* Para Number - Read Only */}
                <Grid item xs={12} md={1}>
                  <TextField
                    value={row.paraNumber || `P${idx + 1}`}
                    label="Para"
                    fullWidth
                    size="small"
                    disabled
                    InputProps={{
                      readOnly: true,
                    }}
                    inputProps={{ autoComplete: 'off' }}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#00798E',
                        fontWeight: 'bold',
                      },
                    }}
                  />
                </Grid>

                {/* First Grid: Year (top) and Type (bottom) */}
                <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Year"
                        required
                        disabled={loading}
                        value={row.yearOfMiscarriage || ''}
                        onChange={(e) => handleRowChange(idx, 'yearOfMiscarriage', e.target.value)}
                      >
                        <MenuItem value="" disabled><em>Select year</em></MenuItem>
                        {YEAR_OPTIONS.map((y) => (
                          <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={row.typeOfMiscarriage}
                          label="Type"
                          onChange={(e) => handleRowChange(idx, 'typeOfMiscarriage', e.target.value)}
                          disabled={loading}
                          sx={{ height: selectBoxHeight }}
                        >
                          {Array.isArray(typeOfMiscarriage) && typeOfMiscarriage.map((opt) => (
                            <MenuItem key={opt.param_group_member_option_id || opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Second Grid: Weeks and D+E (top) */}
                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small" required>
                            <InputLabel>Weeks</InputLabel>
                            <Select
                              value={row.wksOfMiscarriage}
                              label="Weeks"
                              onChange={(e) => handleRowChange(idx, 'wksOfMiscarriage', e.target.value)}
                              disabled={loading}
                              sx={{ height: selectBoxHeight }}
                            >
                              {Array.from({ length: 41 }, (_, i) => i).map((week) => (
                                <MenuItem key={week} value={week}>
                                  {week}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={row.deDone}
                            onChange={(e) => handleRowChange(idx, 'deDone', e.target.checked)}
                            color="primary"
                            disabled={loading || isDEDisabledForType(typeOfMiscarriage.find(opt => opt.value === row.typeOfMiscarriage)?.label)}
                          />
                        }
                        label="D+E Done"
                        labelPlacement="end"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Preg Type - Commented Out */}
                {/* <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Preg Type</InputLabel>
                    <Select
                      value={row.pregType}
                      label="Preg Type"
                      onChange={(e) => handleRowChange(idx, 'pregType', e.target.value)}
                      disabled={loading}
                      sx={{ height: selectBoxHeight }}
                    >
                      {Array.isArray(typeOfPregnancy) && typeOfPregnancy.map((opt) => (
                        <MenuItem key={opt.param_group_member_option_id || opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid> */}

                {/* Third Grid: Remarks RichTextEditor */}
                <Grid item xs={12} md={5} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%' }}>
                    <RichTextEditor
                      value={row.remarks}
                      onChange={(value) => handleRowChange(idx, 'remarks', value)}
                      label="Remarks"
                      height="120px"
                      placeholder="Brief remarks..."
                    />
                  </Box>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12} md={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <IconButton
                    onClick={handleAddRow}
                    disabled={loading}
                    aria-label="Add row"
                    size="medium"
                    sx={{ 
                      color: '#2e7d32', 
                      bgcolor: '#e8f5e9', 
                      '&:hover': { bgcolor: '#c8e6c9' },
                      width: 40,
                      height: 40
                    }}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                  {rows.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveRow(idx)}
                      disabled={loading}
                      aria-label="Remove row"
                      size="medium"
                      sx={{ 
                        color: '#c62828', 
                        bgcolor: '#ffebee', 
                        '&:hover': { bgcolor: '#ffcdd2' },
                        width: 40,
                        height: 40
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Box>
          ))}
          
          <Grid container>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="center" sx={{ mt: 2 }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 32px',
                    background: '#00798E',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  Save Miscarriage History
                </button>
              </MDBox>
            </Grid>
          </Grid>
        </form>
            );
          })()}
      </Box>
      )}
    </MDBox>
  );
}

ObstetricHistoryForm.propTypes = {
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  onDeleteInfertility: PropTypes.func,
  onDeleteMiscarriage: PropTypes.func,
  onEdit: PropTypes.func,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  obstetricHistory: PropTypes.shape({
    id: PropTypes.number,
    infetrtility_type: PropTypes.string,
    para_p1: PropTypes.string,
    para_p2: PropTypes.string,
    no_of_miscarriages: PropTypes.number,
    remarks: PropTypes.string,
    live_issues: PropTypes.string
  }),
  miscarriageHistories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      year_of_miscarriage: PropTypes.number, // Changed from string to number
      type_of_miscarriage: PropTypes.string,
      weeks_of_miscarriage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Allow both types
      de_done: PropTypes.bool,
      preg_type: PropTypes.string,
      remarks: PropTypes.string
    })
  ),
  typeOfMiscarriage: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    param_group_member_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    param_group_member_option_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    param_group_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  typeOfPregnancy: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    param_group_member_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    param_group_member_option_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    param_group_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }))
};

export default ObstetricHistoryForm;
